/* eslint-disable no-console */
import { Auth0Provider } from '@auth0/auth0-react';
import {
  VersionControllerClient,
  WorkflowVersion
} from '@foxden/version-controller-client';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { urlJoin } from 'url-join-ts';

import { PaymentPublicClient } from '../backend-client/payment-public-backend';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Loading from '../components/Loading';
import getEnv from '../utils/getEnv';
import { AuthContext } from './authContext';
import { useAuth } from './AuthProvider';
import localAuth0CacheOptions from './localAuth0CacheOptions';

const {
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_PUBLIC_URL,
  REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
} = getEnv();

/**
 * Note: Auth0 needs explicit access to the history, so we need to get the history
 * with the useHistory hook. Here we're typing Auth0 react-router-dom together.
 *
 * Copied from:
 * https://auth0.com/blog/complete-guide-to-react-user-authentication/
 */
const Auth0ProviderWithHistory: React.FC = ({ children }) => {
  // Use the useLocation hook to access the current location
  const location = useLocation();
  const history = useHistory();
  const { isAuthenticated: isAuthAuthenticated, user } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const quoteId = queryParams.get('quote') ?? undefined;

  const pathname = location?.pathname?.trim();
  const regex = /.*terms\//i;
  const tempQuoteId = regex.test(pathname)
    ? pathname.replace(regex, '')
    : undefined;

  const [versionData, setVersionData] = useState<WorkflowVersion>();
  const [versionDataError, setVersionDataError] = useState();
  const [emailAddress, setEmailAddress] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getEmailAddressError, setGetEmailAddressError] = useState();

  useEffect(() => {
    if (quoteId !== undefined) {
      setIsLoading(true);

      const versionControllerClient = new VersionControllerClient(
        REACT_APP_VERSION_CONTROLLER_GRAPHQL_URL
      );
      versionControllerClient
        .getVersionFromQuote(quoteId)
        .then((data) => {
          setVersionData(data);
          setIsLoading(false);
        })
        .catch((err) => {
          setVersionDataError(err);
          setIsLoading(false);
        });
    }
  }, [quoteId]);

  useEffect(() => {
    if (quoteId !== undefined) {
      setIsLoading(true);

      const paymentPublicClient = new PaymentPublicClient(
        versionData?.paymentBackendVersion
      );

      paymentPublicClient
        .getEmailAddress(quoteId)
        .then((data) => {
          setEmailAddress(data);
          setIsLoading(false);
        })
        .catch((err) => {
          setGetEmailAddressError(err);
          setIsLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, versionData]);

  if (tempQuoteId && !tempQuoteId.includes('?quote=')) {
    window.location.replace(
      urlJoin(window.location.origin, REACT_APP_PUBLIC_URL) +
        `/terms/?quote=${tempQuoteId}`
    );
    return null;
  }

  const domain = REACT_APP_AUTH0_DOMAIN;
  const clientId = REACT_APP_AUTH0_CLIENT_ID;

  const origin = urlJoin(window.location.origin, REACT_APP_PUBLIC_URL) + '/';

  // Auth0Provider is the STABLE outer wrapper for both the loading window and
  // the loaded app. Previously the loading state returned a bare <div>, so the
  // provider was unmounted and remounted every time `isLoading` toggled. During
  // that window children such as ProtectedRoute -> withAuthenticationRequired
  // invoked Auth0 stub methods (e.g. loginWithRedirect) while no provider was
  // mounted, throwing "You forgot to wrap your component in <Auth0Provider>".
  // Keeping the provider mounted across the toggle prevents that. See NCP-4776.
  //
  // WHY THE `key`: @auth0/auth0-react v1 constructs its Auth0Client exactly
  // once, in a useState initializer on the provider's FIRST mount, and never
  // reacts to later prop changes. But `login_hint` (the customer email) is
  // fetched asynchronously and arrives AFTER that first mount, so a provider
  // mounted once would be frozen with `login_hint: undefined` — the Auth0 page
  // then shows the generic identifier screen with an empty email and never
  // sends the passwordless OTP. Keying on the email makes React recreate the
  // provider (and thus the client) exactly once, when `emailAddress` resolves
  // from undefined to a value, so the client is built WITH the hint. The swap
  // is a single atomic commit — children are never left without a provider, so
  // NCP-4776's "forgot to wrap your component" fix is preserved.
  const withAuth0Provider = (node: React.ReactNode) => (
    <Auth0Provider
      key={emailAddress ?? 'no-login-hint'}
      domain={domain}
      clientId={clientId}
      login_hint={emailAddress}
      redirectUri={origin}
      {...localAuth0CacheOptions}
      onRedirectCallback={(appState) => {
        const revisedReturnTo = appState?.returnTo
          ? appState?.returnTo.replace(`${REACT_APP_PUBLIC_URL}`, '')
          : window.location.pathname.replace(`${REACT_APP_PUBLIC_URL}`, '');
        history.replace(revisedReturnTo || window.location.pathname);
      }}
    >
      {node}
    </Auth0Provider>
  );

  if (isLoading) {
    return withAuth0Provider(
      <div className="grid grid-rows-main h-full w-full text-quartary">
        <Header usedWithRoutes={false} />
        <div
          className="flex"
          role="status"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading"
        >
          <Loading />
        </div>
        <Footer />
      </div>
    );
  }

  if (versionDataError) {
    throw Error('Failed when trying to get version data');
  }

  if (getEmailAddressError) {
    throw Error('Failed when trying to get email address');
  }

  // Session-cookie auth path: Auth0 is intentionally not used here.
  if (isAuthAuthenticated) {
    return (
      <AuthContext.Provider value={{ login_hint: user?.email }}>
        {children}
      </AuthContext.Provider>
    );
  }

  return withAuth0Provider(
    <AuthContext.Provider value={{ login_hint: emailAddress }}>
      {children}
    </AuthContext.Provider>
  );
};

export default Auth0ProviderWithHistory;
